import { HelpCircle, House, ListChecks, Settings, UserCog } from 'lucide-react';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@bliks-io/ui';
import { usePartnerAdminAccess } from '@/pages/partner/usePartnerAdminAccess';
import { useMyOpenTasks } from '@/api/queries/useMyOpenTasks';
import { NavMain, type NavMainItem } from '@/layout/nav/NavMain';
import { buildAppNavItems } from '@/layout/nav/buildAppNavItems';
import { useInstalledApps } from '@/api/queries/useInstalledApps';

// NavMain builds links as `/<firstPathSegment><item.url>`, so inside the
// `/partner` area `url: '/leads'` resolves to the leads list.

const homeNavItem: NavMainItem = {
  title: 'partner.home.title',
  url: '/',
  icon: House
};

// The Aufgaben handed to the signed-in partner member; the badge counts the
// open ones, like "Meine Aufgaben" in the customer app.
const tasksNavItem: NavMainItem = {
  title: 'partner.tasks.title',
  url: '/tasks',
  icon: ListChecks
};

// Everything a selling partner works with is the sales app (SAL-10): leads,
// the call list, the calculator, the Info Hub and the programme — one entry,
// listed under "Apps" the way the customer sidebar lists apps.

// Settings is the only partner_admin-only nav entry. It's a group, so NavMain
// renders the side pop-out of its tabs — profile and user management —
// mirroring the customer app's settings area (where User Management also lives
// under Settings).
const adminNavItems: NavMainItem[] = [
  {
    title: 'common.Sidebar.Settings',
    url: '/organization',
    icon: Settings,
    items: [
      {
        title: 'common.Sidebar.Settings',
        url: '/organization',
        icon: Settings
      },
      {
        title: 'partner.settings.tabs.users',
        url: '/organization/users',
        icon: UserCog
      }
    ]
  }
];

export function PartnerNavSidebar() {
  const { t, i18n } = useTranslation();
  const { isPartnerAdmin } = usePartnerAdminAccess();
  const { data: openTasks } = useMyOpenTasks();
  const openTaskCount = openTasks?.length ?? 0;
  // The apps a partner tenant holds (SALES-68: the sales app is the first),
  // listed the way the customer sidebar lists them — only for whoever may
  // open them, which the installed-apps list already decides.
  const { data: installedApps } = useInstalledApps();
  const navigation: NavMainItem[] = [
    homeNavItem,
    openTaskCount > 0 ? { ...tasksNavItem, badge: openTaskCount } : tasksNavItem,
    ...(isPartnerAdmin ? adminNavItems : [])
  ];
  const appNavItems = useMemo<NavMainItem[]>(
    () => buildAppNavItems(installedApps, i18n.language),
    [installedApps, i18n.language]
  );

  return (
    <TooltipProvider delayDuration={0}>
      <nav className="flex flex-col w-14 bg-sidebar text-sidebar-foreground shrink-0">
        {/* Main navigation */}
        <div className="flex-1 overflow-y-auto">
          <NavMain items={navigation} />
          {appNavItems.length > 0 && (
            <NavMain title={t('common.Sidebar.Apps')} items={appNavItems} />
          )}
        </div>

        {/* Footer */}
        <div className="p-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href="https://help.bliks.io"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full h-10 transition-colors rounded-md hover:bg-sidebar-accent"
              >
                <HelpCircle className="h-[21px] w-[21px] text-fg-secondary" strokeWidth={1.5} />
              </a>
            </TooltipTrigger>
            <TooltipContent side="right" align="center">
              {t('common.Help')}
            </TooltipContent>
          </Tooltip>
        </div>
      </nav>
    </TooltipProvider>
  );
}
